import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { Card, CardContent, Box, TextField, Button, Autocomplete, Popover, Typography } from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import { LocalizationProvider, DatePicker } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';
import { DEPARTMENT_CHOICES } from '../../utils/employeeConstants';
import EmployeeSearchField from '../common/EmployeeSearchField';

const STATUS_CHOICES = [
  { value: 'All', label: 'All Status' },
  { value: 'Present', label: 'Present' },
  { value: 'Absent', label: 'Absent' },
  { value: 'On Leave', label: 'On Leave' },
  { value: 'Remote Work', label: 'Remote Work' },
  { value: 'Late', label: 'Late' },
  { value: 'Half Day', label: 'Half Day' },
];

const AttendanceFilters = ({
  searchTerm,
  employeeFilter,
  departmentFilter,
  statusFilter,
  dateRange,
  onSearchChange,
  onFilterChange,
  onDateRangeChange,
  onClearFilters,
  filterOptions = {},
}) => {
  const [calendarAnchor, setCalendarAnchor] = useState(null);

  const statusChoices = filterOptions.statuses
    ? [
        { value: 'All', label: 'All Status' },
        ...filterOptions.statuses.map((status) => ({
          value: status.status,
          label: status.status,
        })),
      ]
    : STATUS_CHOICES;

  const departmentOptions = filterOptions.departments
    ? [
        { value: 'All', label: 'All Departments' },
        ...filterOptions.departments.map((dept) => ({ value: dept, label: dept })),
      ]
    : [{ value: 'All', label: 'All Departments' }, ...DEPARTMENT_CHOICES];

  const handleOpenCalendar = (event) => {
    setCalendarAnchor(event.currentTarget);
  };

  const handleCloseCalendar = () => {
    setCalendarAnchor(null);
  };

  const handleDateSelect = (newDate) => {
    if (!newDate) return;
    const formatted = dayjs(newDate).format('YYYY-MM-DD');
    onDateRangeChange({
      startDate: formatted,
      endDate: formatted,
      preset: 'single',
    });
    handleCloseCalendar();
  };

  const displayDate = dateRange?.startDate ? dayjs(dateRange.startDate).format('MMM DD, YYYY') : 'Select Date';

  return (
    <>
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Box
            sx={{
              display: 'flex',
              flexWrap: 'wrap',
              gap: 2,
              alignItems: 'center',
            }}
          >
            <Box
              sx={{
                flex: { xs: '1 1 100%', sm: '1 1 calc(50% - 8px)', md: '1 1 200px' },
                minWidth: { xs: '100%', sm: '200px', md: '150px' },
              }}
            >
              <TextField
                fullWidth
                placeholder="Search attendance..."
                value={searchTerm}
                onChange={onSearchChange}
                size="small"
                InputProps={{
                  startAdornment: <SearchIcon sx={{ mr: 1, color: 'text.secondary' }} />,
                }}
              />
            </Box>
            <Box
              sx={{
                flex: { xs: '1 1 100%', sm: '1 1 calc(50% - 8px)', md: '1 1 180px' },
                minWidth: { xs: '100%', sm: '180px', md: '150px' },
              }}
            >
              <EmployeeSearchField
                value={employeeFilter}
                emptyValue="All"
                onChange={(id) => onFilterChange('employee', id)}
                apiFilters={{
                  department: departmentFilter !== 'All' ? departmentFilter : undefined,
                }}
                label="Employee"
              />
            </Box>
            <Box
              sx={{
                flex: { xs: '1 1 100%', sm: '1 1 calc(50% - 8px)', md: '1 1 180px' },
                minWidth: { xs: '100%', sm: '180px', md: '150px' },
              }}
            >
              <Autocomplete
                key={departmentFilter}
                size="small"
                fullWidth
                defaultValue={
                  departmentOptions.find((opt) => opt.value === departmentFilter) || departmentOptions[0] || null
                }
                onChange={(event, newValue) => {
                  onFilterChange('department', newValue?.value || 'All');
                }}
                options={departmentOptions}
                getOptionLabel={(option) => option?.label || ''}
                isOptionEqualToValue={(option, value) => option?.value === value?.value}
                renderInput={(params) => <TextField {...params} label="Department" />}
                clearOnEscape
              />
            </Box>
            <Box
              sx={{
                flex: { xs: '1 1 100%', sm: '1 1 calc(50% - 8px)', md: '1 1 150px' },
                minWidth: { xs: '100%', sm: '150px', md: '120px' },
              }}
            >
              <Autocomplete
                key={statusFilter}
                size="small"
                fullWidth
                defaultValue={statusChoices.find((opt) => opt.value === statusFilter) || statusChoices[0] || null}
                onChange={(event, newValue) => {
                  onFilterChange('status', newValue?.value || 'All');
                }}
                options={statusChoices}
                getOptionLabel={(option) => option?.label || ''}
                isOptionEqualToValue={(option, value) => option?.value === value?.value}
                renderInput={(params) => <TextField {...params} label="Status" />}
                clearOnEscape
              />
            </Box>
            <Box
              sx={{
                flex: { xs: '1 1 100%', sm: '1 1 100%', md: '1 1 180px' },
                minWidth: { xs: '100%', sm: '100%', md: '180px' },
              }}
            >
              <Button
                fullWidth
                variant="outlined"
                onClick={handleOpenCalendar}
                startIcon={<CalendarTodayIcon />}
                sx={{
                  height: '40px',
                  justifyContent: 'flex-start',
                  textTransform: 'none',
                  color: 'text.secondary',
                  borderColor: 'text.secondary',
                  '&:hover': {
                    borderColor: 'black',
                  },
                }}
              >
                {displayDate}
              </Button>
            </Box>
            <Box
              sx={{
                flex: { xs: '1 1 100%', sm: '0 0 auto', md: '0 0 auto' },
                minWidth: { xs: '100%', sm: 'auto', md: 'auto' },
              }}
            >
              <Button
                variant="outlined"
                onClick={onClearFilters}
                size="small"
                sx={{ height: '40px', minWidth: '120px' }}
              >
                Clear Filters
              </Button>
            </Box>
          </Box>
        </CardContent>
      </Card>

      <Popover
        open={Boolean(calendarAnchor)}
        anchorEl={calendarAnchor}
        onClose={handleCloseCalendar}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
      >
        <Box sx={{ p: 2 }}>
          <Typography variant="subtitle2" sx={{ mb: 1.5 }}>
            Select Date
          </Typography>
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <DatePicker
              value={dateRange?.startDate ? dayjs(dateRange.startDate) : null}
              onChange={handleDateSelect}
              slotProps={{
                textField: {
                  fullWidth: true,
                  size: 'small',
                },
              }}
            />
          </LocalizationProvider>
        </Box>
      </Popover>
    </>
  );
};

AttendanceFilters.propTypes = {
  searchTerm: PropTypes.string.isRequired,
  employeeFilter: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  departmentFilter: PropTypes.string.isRequired,
  statusFilter: PropTypes.string.isRequired,
  dateRange: PropTypes.shape({
    startDate: PropTypes.string,
    endDate: PropTypes.string,
    preset: PropTypes.string,
  }).isRequired,
  onSearchChange: PropTypes.func.isRequired,
  onFilterChange: PropTypes.func.isRequired,
  onDateRangeChange: PropTypes.func.isRequired,
  onClearFilters: PropTypes.func.isRequired,
  filterOptions: PropTypes.shape({
    employees: PropTypes.arrayOf(
      PropTypes.shape({
        id: PropTypes.number.isRequired,
        full_name: PropTypes.string.isRequired,
      })
    ),
    statuses: PropTypes.arrayOf(
      PropTypes.shape({
        status: PropTypes.string.isRequired,
      })
    ),
  }),
};

export default AttendanceFilters;
